import React, { useState, useEffect, useMemo } from 'react';
import './MatchOutfitSelector.css';
import { CategorizedItems } from '../../../../services/FashMatchService';

// Reusing Product interface or defining a minimal compatible one
interface Product {
    id?: string;
    sku?: string;
    title?: string;
    name?: string;
    imageUrl?: string;
    image?: string; // Support varying image property names
}

interface MatchOutfitSelectorProps {
    categorizedIds: CategorizedItems;
    allProducts: Product[];
    initialSelection?: string[]; // SKUs
    onGenerate: (selectedSkus: string[]) => void;
    onClose: () => void;
}

type CategoryKey = keyof Omit<CategorizedItems, 'other'>;

const CATEGORY_LABELS: Record<CategoryKey, string> = {
    outerwear: 'Dış Giyim',
    top: 'Üst Giyim',
    bottom: 'Alt Giyim',
    shoes: 'Ayakkabı',
    accessory: 'Aksesuar'
};

const CATEGORIES: CategoryKey[] = ['outerwear', 'top', 'bottom', 'shoes', 'accessory'];

export const MatchOutfitSelector: React.FC<MatchOutfitSelectorProps> = ({
    categorizedIds,
    allProducts,
    initialSelection = [],
    onGenerate,
    onClose
}) => {
    // State to track selection per category (SKU or null)
    const [selections, setSelections] = useState<Record<CategoryKey, string | null>>({
        outerwear: null,
        top: null,
        bottom: null,
        shoes: null,
        accessory: null
    });

    const [activeDropdown, setActiveDropdown] = useState<CategoryKey | null>(null);

    // Initialize selection logic
    useEffect(() => {
        if (!initialSelection || initialSelection.length === 0) return;

        const newSelections = { ...selections };

        // Map initial SKUs to categories
        initialSelection.forEach(sku => {
            // Find which category this SKU belongs to
            for (const cat of CATEGORIES) {
                if (categorizedIds[cat]?.includes(sku)) {
                    newSelections[cat] = sku;
                    break;
                }
            }
        });

        // Auto-select if a category has only 1 item and no selection yet


        // Loop through all categories to find single items
        CATEGORIES.forEach(cat => {
            const items = categorizedIds[cat];
            if (items && items.length === 1 && !newSelections[cat]) {
                // Auto-select if only 1 item and not already selected
                newSelections[cat] = items[0];
            }
        });

        setSelections(newSelections);
    }, [categorizedIds]); // eslint-disable-line react-hooks/exhaustive-deps

    // Helper to get product details
    const getProduct = (sku: string): Product | undefined => {
        return allProducts.find(p => (p.sku || p.id) === sku);
    };

    const handleSelect = (category: CategoryKey, sku: string) => {
        setSelections(prev => ({
            ...prev,
            [category]: prev[category] === sku ? null : sku // Toggle selection
        }));
        setActiveDropdown(null);
    };

    const handleGenerate = () => {
        const selectedSkus = Object.values(selections).filter(Boolean) as string[];
        onGenerate(selectedSkus);
    };

    // Validation: At least 2 distinct categories must be selected
    const isValid = useMemo(() => {
        const selectedCount = Object.values(selections).filter(Boolean).length;
        return selectedCount >= 2;
    }, [selections]);

    // Close dropdown when clicking outside (simple implementation)
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.category-dropdown')) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="mirror-selector-overlay">
            <div className="mirror-selector-container">
                <h2 className="mirror-selector-title">Kombinini Oluştur</h2>
                <p className="mirror-selector-subtitle">Kategorilerden parçaları seçerek stilini tamamla.</p>

                <div className="mirror-selector-form">
                    {CATEGORIES.map(category => {
                        const availableSkus = categorizedIds[category] || [];

                        // Skip rendering category if no items available (optional decision, but keeping it visible as empty state might be better or hiding it)
                        // Decision: Show it but as disabled/empty if no items, to maintain persistent UI structure
                        if (availableSkus.length === 0) return null;

                        const selectedSku = selections[category];
                        const selectedProduct = selectedSku ? getProduct(selectedSku) : null;
                        const isOpen = activeDropdown === category;

                        return (
                            <div key={category} className="selector-category">
                                <span className="category-label">{CATEGORY_LABELS[category]}</span>

                                <div className="category-dropdown">
                                    <div
                                        className={`dropdown-trigger ${isOpen ? 'active' : ''}`}
                                        onClick={() => setActiveDropdown(isOpen ? null : category)}
                                    >
                                        {selectedProduct ? (
                                            <div className="selected-item-preview">
                                                <img
                                                    src={selectedProduct.imageUrl || selectedProduct.image || (Array.isArray((selectedProduct as any).images) ? (selectedProduct as any).images[0] : '/placeholder.jpg')}
                                                    alt="Thumbnail"
                                                    className="preview-thumb"
                                                />
                                                <div className="preview-info">
                                                    <span className="preview-title">{selectedProduct.title || selectedProduct.name}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="placeholder-text">Seçim yapılmadı</span>
                                        )}
                                        <span className="material-symbols-outlined dropdown-arrow">expand_more</span>
                                    </div>

                                    {isOpen && (
                                        <div className="dropdown-options">
                                            {availableSkus.map(sku => {
                                                const product = getProduct(sku);
                                                if (!product) return null;
                                                const isSelected = selectedSku === sku;

                                                return (
                                                    <div
                                                        key={sku}
                                                        className={`option-item ${isSelected ? 'selected' : ''}`}
                                                        onClick={() => handleSelect(category, sku)}
                                                    >
                                                        <img
                                                            src={product.imageUrl || product.image || '/placeholder.jpg'}
                                                            alt="Option"
                                                            style={{ width: 32, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                                        />
                                                        <span style={{ fontSize: '0.9rem', color: '#333' }}>
                                                            {product.title || product.name || 'İsimsiz Ürün'}
                                                        </span>
                                                        {isSelected && <span className="material-symbols-outlined" style={{ marginLeft: 'auto', fontSize: '1.2rem' }}>check</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="selector-actions">
                    <button className="action-btn btn-secondary" onClick={onClose}>
                        İptal
                    </button>
                    <button
                        className="action-btn btn-primary"
                        onClick={handleGenerate}
                        disabled={!isValid}
                    >
                        Kombini Oluştur
                    </button>
                </div>
            </div>
        </div>
    );
};
